'use client';

import { useEffect } from 'react';
import { useNinetailed } from '@ninetailed/experience.js-react';
import { ONETRUST_CATEGORIES, onConsentChange } from '@/libs/oneTrust';

/**
 * Bridges the OneTrust consent dispatcher to the Ninetailed Privacy Plugin.
 *
 * Per the plugin's docs, consent is toggled by calling:
 *   window.ninetailed.consent(true)   // sets __nt-consent__: "accepted"
 *   window.ninetailed.consent(false)  // clears __nt-consent__
 *
 * The plugin exposes that global at the end of its async `initialize`, so we
 * defer the OneTrust subscription until `ninetailed.onIsInitialized` fires
 * (the analytics-js `ready` event). The plugin's published `Window.ninetailed`
 * type omits `consent`, so we widen `window.ninetailed` through a structural
 * alias to call it without a type assertion.
 */
type NinetailedWithConsent = {
  reset: () => void;
  consent?: (accepted: boolean) => void;
};

export function NinetailedConsentSync(): null {
  const ninetailed = useNinetailed();

  useEffect(() => {
    let detached = false;
    let unsubscribe: (() => void) | undefined;

    ninetailed.onIsInitialized(() => {
      if (detached) return;
      const ninetailedGlobal: NinetailedWithConsent | undefined = window.ninetailed;
      if (!ninetailedGlobal?.consent) return;

      unsubscribe = onConsentChange(activeGroups => {
        ninetailedGlobal.consent?.(activeGroups.includes(ONETRUST_CATEGORIES.PERFORMANCE));
      });
    });

    return () => {
      detached = true;
      unsubscribe?.();
    };
  }, [ninetailed]);

  return null;
}
